/**
 * Hive Capabilities tab (Sprint 267 — REQ-CAP-002 §4, ADR-113; ISS-572).
 *
 * Vista compacta (patrón "Skills del hive") + modal de configuración (patrón
 * "Skills scope"): decisiones explícitas del hive con tristate (heredar /
 * activada / desactivada) y settings del hive (override de los del tenant).
 * Persiste vía `PUT /api/v1/tenants/{t}/capabilities/hives/{id}`.
 *
 * Semántica backend: `enabled` null + sin settings ⇒ se BORRA la decisión del
 * hive (vuelve a heredar del tenant).
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';
import { CapabilitiesModal } from './CapabilitiesModal';
import { CapabilityCompactList } from './CapabilityCompactList';
import { CapabilitySettingsForm } from './CapabilitySettingsForm';
import { capabilityTitle } from './CapabilityRow';
import { useCapabilitiesCatalog, useHiveCapabilities, useSetHiveCapabilities } from './hooks';
import type { HiveCapabilityDecisionInput, SettingsValues } from './types';

interface DraftDecision {
  enabled: boolean | null;
  settings: SettingsValues;
}

interface Props {
  hiveId: string | undefined;
}

export function HiveCapabilitiesTab({ hiveId }: Props) {
  const { t } = useI18n();
  const catalog = useCapabilitiesCatalog();
  const current = useHiveCapabilities(hiveId);
  const saveHive = useSetHiveCapabilities(hiveId);

  const [draft, setDraft] = useState<Record<string, DraftDecision>>({});
  const [dirty, setDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!dirty && current.data) {
      const next: Record<string, DraftDecision> = {};
      for (const [slug, decision] of Object.entries(current.data.decisions ?? {})) {
        next[slug] = {
          enabled: decision.enabled ?? null,
          settings: (decision.settings as SettingsValues) ?? {},
        };
      }
      setDraft(next);
    }
  }, [current.data, dirty]);

  if (!hiveId) {
    return null;
  }
  if (catalog.isLoading || current.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }
  if (catalog.isError || current.isError) {
    return (
      <Card className="p-6 mt-4 text-sm text-red-600 dark:text-red-400">
        {t('admin.capabilities.loadError') || 'Could not load capability configuration.'}
      </Card>
    );
  }

  const effective = current.data?.effective ?? {};
  const decisionOf = (slug: string): DraftDecision => draft[slug] ?? { enabled: null, settings: {} };

  const setDecision = (slug: string, enabled: boolean | null) => {
    setDraft((prev) => ({ ...prev, [slug]: { ...decisionOf(slug), enabled } }));
    setDirty(true);
  };

  const setSettings = (slug: string, settings: SettingsValues) => {
    setDraft((prev) => ({ ...prev, [slug]: { ...decisionOf(slug), settings } }));
    setDirty(true);
  };

  const save = async () => {
    try {
      const decisions: HiveCapabilityDecisionInput[] = (catalog.data ?? []).map((entry) => {
        const current = decisionOf(entry.slug);
        const hasSettings = current.settings && Object.keys(current.settings).length > 0;
        return {
          slug: entry.slug,
          enabled: current.enabled,
          settings: hasSettings ? current.settings : null,
        };
      });
      await saveHive.mutateAsync(decisions);
      setDirty(false);
      setModalOpen(false);
      toast.success(t('admin.capabilities.saved') || 'Capability configuration saved');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <Card className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.capabilities.title') || 'Capabilities'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('admin.capabilities.manageDescription') ||
                'Activate or deactivate capabilities and adjust their per-level settings.'}
            </p>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            {t('admin.capabilities.manage') || 'Manage capabilities'}
          </Button>
        </div>
      </Card>

      <CapabilityCompactList catalog={catalog.data ?? []} effective={effective} />

      <CapabilitiesModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={t('admin.capabilities.title') || 'Capabilities'}
        description={t('admin.capabilities.manageDescription') || ''}
        catalog={catalog.data ?? []}
        effective={effective}
        dirty={dirty}
        renderControl={(entry) => {
          const decision = decisionOf(entry.slug);
          return (
            <select
              aria-label={capabilityTitle(t, entry)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              value={
                decision.enabled === null
                  ? 'inherit'
                  : decision.enabled
                    ? 'enabled'
                    : 'disabled'
              }
              onChange={(event) =>
                setDecision(
                  entry.slug,
                  event.target.value === 'inherit' ? null : event.target.value === 'enabled',
                )
              }
            >
              <option value="inherit">
                {t('admin.capabilities.decision.inherit') || 'Inherit'}
              </option>
              <option value="enabled">
                {t('admin.capabilities.decision.enabled') || 'Enabled'}
              </option>
              <option value="disabled">
                {t('admin.capabilities.decision.disabled') || 'Disabled'}
              </option>
            </select>
          );
        }}
        renderExtra={(entry) => {
          const decision = decisionOf(entry.slug);
          const hasSchema = !!(
            entry.config_schema?.properties &&
            Object.keys(entry.config_schema.properties).length > 0
          );
          if (!hasSchema) return undefined;
          return (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('admin.capabilities.hiveSettings') ||
                  'Hive settings (override tenant defaults)'}
              </p>
              <CapabilitySettingsForm
                schema={entry.config_schema}
                values={decision.settings}
                onChange={(values) => setSettings(entry.slug, values)}
              />
            </div>
          );
        }}
        footer={
          <Button onClick={save} disabled={!dirty || saveHive.isPending}>
            {saveHive.isPending && <Spinner className="mr-2 h-4 w-4" />}
            {t('admin.capabilities.save') || 'Save'}
          </Button>
        }
      />
    </div>
  );
}
